import { createContext, useContext, useEffect, useState } from "react"
import tokenService from "../services/token-service"

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authToken, setAuthToken] = useState(null)
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
  const [user, setUser] = useState(null)

  const refreshUser = async () => {
    const token = tokenService.getToken();
    const storedUser = tokenService.getUserDetails();
    const userId = storedUser?._id || storedUser?.id;

    if (!token) return;

    if (!userId) {
      logout();
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const result = await response.json();
        const updatedUser = result?.data?.user || result?.data;
        if (updatedUser) {
          tokenService.setUserDetails(updatedUser);
          setUser(updatedUser);
        }
      } else {
        // Clear invalid or expired session on any non-OK status (401, 403, 404, etc.)
        logout();
      }
    } catch (err) {
      console.error("Error refreshing user profile:", err);
    }
  };

  // 🔹 On mount, check if token & stored user exist in storage and verify with server
  useEffect(() => {
    const token = tokenService.getToken()
    const storedUser = tokenService.getUserDetails()

    if (token && storedUser) {
      setAuthToken(token)
      setUser(storedUser)
      setIsAuthenticated(true)
      refreshUser()
    } else if (token || storedUser) {
      // Clear corrupt or partial storage
      logout()
    }
  }, [])

  // 🔹 Handle login
  const login = (token, userData) => {
    tokenService.setToken(token)
    tokenService.setUserDetails(userData)

    setAuthToken(token)
    setUser(userData)
    setIsAuthenticated(true)
    setIsLoginModalOpen(false)
  }

  // 🔹 Handle logout
  const logout = () => {
    tokenService.removeToken()
    tokenService.removeUserDetails();
    tokenService.removeOnboardingStatus();

    setAuthToken(null)
    setUser(null)
    setIsAuthenticated(false)
    setIsLoginModalOpen(true) // automatically open modal after logout
  }
  return (
    <AuthContext.Provider
      value={{
        authToken,
        isAuthenticated,
        user,
        isLoginModalOpen,
        setIsLoginModalOpen,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)

